import { useEffect } from 'react';
import {
  useQuery, useQueryClient, useInfiniteQuery, UseQueryResult, UseInfiniteQueryResult, InfiniteData,
} from '@tanstack/react-query';
import { getNotesByQuery } from '../../services/NotesService';
import { NotesResponse } from '../../types/Notes';
import { FeaturesManagement } from '../../../featuresControl';
import { mergeNewestIntoFirstPage } from '../../../utils/queryFilters';

function useGetNotesByQuery(fleetId: string, query: string): UseQueryResult<NotesResponse> {
  return useQuery(
    ['notes', fleetId, query],
    async (): Promise<NotesResponse> => getNotesByQuery(fleetId, query),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
    },
  );
}

export function useGetNotesByQueryLazy(fleetId: string, payload?: any, query?: string, deps?: any): UseInfiniteQueryResult<NotesResponse> {
  const queryClient = useQueryClient();
  const queryKey = ['notes-lazy', fleetId, deps];

  const infiniteQuery = useInfiniteQuery(
    queryKey,
    async ({ pageParam = query }): Promise<NotesResponse> => getNotesByQuery(fleetId, pageParam),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    },
  );

  const { data: latestPage } = useQuery(
    ['notes-lazy-latest', fleetId, deps, query],
    async (): Promise<NotesResponse> => getNotesByQuery(fleetId, query as string),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchInterval: FeaturesManagement.productivity.refetchIntervalLazyLists,
      enabled: !!infiniteQuery.data,
    },
  );

  useEffect(() => {
    if (!latestPage) return;
    queryClient.setQueryData<InfiniteData<NotesResponse>>(
      queryKey,
      (existing) => mergeNewestIntoFirstPage(existing, latestPage),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPage]);

  return infiniteQuery;
}

export default useGetNotesByQuery;
